import { TestTube2, AlertTriangle } from "lucide-react";

/**
 * Sticky top-of-screen banner that warns operators when the app is NOT
 * running in production. Read from `REACT_APP_ENV` injected at build time
 * by the CodeBuild buildspec (KREDZ_ENV → REACT_APP_ENV).
 *
 * - `production`: returns null (no banner).
 * - `staging`: yellow banner — staging environment with sandbox StarkBank.
 * - `dev` / unset / anything else: blue banner — local dev / unknown env.
 *
 * The banner uses fixed positioning + a CSS variable on `<html>` so the rest
 * of the app can offset content if needed (`var(--env-banner-height)`).
 */

const ENV = (process.env.REACT_APP_ENV || "").toLowerCase();
const IS_PROD = ENV === "production" || ENV === "prod";

const VARIANTS = {
  staging: {
    bg: "bg-amber-500",
    fg: "text-amber-950",
    icon: TestTube2,
    label: "SANDBOX",
    message: "Ambiente de teste — StarkBank em sandbox, dados isolados. Não cadastre clientes ou colaboradores reais aqui.",
    testId: "env-banner-staging",
  },
  dev: {
    bg: "bg-blue-500",
    fg: "text-blue-50",
    icon: AlertTriangle,
    label: "DEV",
    message: "Ambiente de desenvolvimento local — não use dados reais.",
    testId: "env-banner-dev",
  },
  unknown: {
    bg: "bg-slate-600",
    fg: "text-slate-50",
    icon: AlertTriangle,
    label: "NON-PROD",
    message: "Ambiente não-produtivo — verifique a URL antes de lançar dados sensíveis.",
    testId: "env-banner-unknown",
  },
};

export default function EnvironmentBanner() {
  if (IS_PROD) return null;
  const variant =
    ENV === "staging" ? VARIANTS.staging :
    ENV === "dev" || ENV === "development" || ENV === "" ? VARIANTS.dev :
    VARIANTS.unknown;
  const Icon = variant.icon;

  return (
    <div
      data-testid={variant.testId}
      role="status"
      aria-live="polite"
      className={`${variant.bg} ${variant.fg} fixed top-0 inset-x-0 z-[9999] flex items-center justify-center gap-2 py-1.5 px-4 text-xs font-semibold tracking-wide shadow-md`}
      style={{ "--env-banner-height": "32px" }}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <span className="uppercase font-bold tracking-widest">{variant.label}</span>
      <span aria-hidden className="opacity-50">·</span>
      <span className="opacity-90 truncate max-w-[60vw] sm:max-w-none">{variant.message}</span>
    </div>
  );
}

export const ENV_BANNER_HEIGHT_PX = IS_PROD ? 0 : 32;
